import type { ReactNode } from "react";
import { Icon } from "./Icon";
import styles from "./ListItem.module.css";

interface ListItemProps {
  /** Title — Body 17px regular. */
  title: string;
  /** Optional subtitle — Footnote 15px muted. */
  subtitle?: string | undefined;
  /**
   * Right-side composition. Pass any node (e.g. a DaySummary) to
   * replace the default meta + chevron. Falls back to `meta + chevron`
   * when omitted.
   */
  children?: ReactNode | undefined;
  /**
   * Default right-side label text (e.g. "(7)" spot count). Only
   * rendered when `children` is omitted. The chevron always renders
   * alongside it.
   */
  meta?: string | undefined;
  /**
   * Surface the row sits on — drives the hover pill colour (flat variant only).
   * - `default` (default) — uses `--action-ghost-hover`
   * - `elevated` — uses `--action-elevated-hover`, for items inside a
   *   surface-raised card like the search modal.
   */
  surface?: "default" | "elevated";
  /**
   * Visual style.
   * - `flat` (default) — transparent row with a bottom border-faint
   *   divider; ::before hover plate per `surface`.
   * - `card` — rounded card with `--action-raised` bg, no divider; hover
   *   swaps to `--action-raised-hover`. Used for the redesigned region
   *   overview list. `surface` is ignored in this variant.
   */
  variant?: "flat" | "card";
  /**
   * Fixed-height (92px) row with overflow clipped, used for search
   * results where the right-side `DaySummary` (72×72) would otherwise
   * make rows taller than the rest of the list. Off by default.
   */
  compact?: boolean;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}

/**
 * Tappable list row — Figma node 98:7077. Title + optional subtitle
 * on the left, composable right-side content on the right. Hover state
 * uses an absolutely-positioned `::before` plate that extends 8px
 * beyond the row bounds (Figma node 114:5482) so the row visually
 * "lifts" above the list. Bottom border-faint divider per row.
 */
export function ListItem({
  title,
  subtitle,
  children,
  meta,
  surface = "default",
  variant = "flat",
  compact = false,
  onClick,
  className,
}: ListItemProps) {
  const interactive = !!onClick;
  return (
    <button
      type="button"
      className={[
        styles.listItem,
        styles[`variant-${variant}`],
        variant === "flat" && styles[`surface-${surface}`],
        compact && styles.compact,
        !interactive && styles.disabled,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      disabled={!interactive}
    >
      <div className={styles.text}>
        <span className={styles.title}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
      </div>
      <div className={styles.right}>
        {children ?? (
          <>
            {meta && <span className={styles.meta}>{meta}</span>}
            {variant !== "card" && (
              <Icon name="chevron-right" className={styles.chevron} />
            )}
          </>
        )}
      </div>
    </button>
  );
}
